import { group } from 'console';
import {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from '../data/models/permission.model';
import { UserGroup } from '../data/models/user-group.model';
import { UserRole } from '../data/models/user-role.model';

class SecurityUtils {
  private readonly PERMISSION_VAR = 'PermissionID_';
  private readonly GROUP_VAR = 'GroupID_';

  public getPermissionName(
    permission: Permission,
    resources: PermissionResource[],
    actions: PermissionAction[],
    scopes: PermissionScope[],
  ): string {
    const resource =
      resources.find(
        (r) => r.permissionResourceID === permission.permissionResourceID,
      )?.permissionResourceBC || '';
    const action =
      actions.find(
        (r) => r.permissionActionID === permission.permissionActionID,
      )?.permissionActionBC || '';
    const scope =
      scopes.find((r) => r.permissionScopeID === permission.permissionScopeID)
        ?.permissionScopeBC || '';
    return `${resource}.${action}.${scope}`;
  }

  public getPermissionDefaults(permission: Permission | null | undefined): any {
    const defaults: { [key: string]: any } = {
      ResourceID: permission?.permissionResourceID ?? '',
      ActionID: permission?.permissionActionID ?? '',
      ScopeID: permission?.permissionScopeID ?? '',
      Description: permission?.description || '',
      IsActive: permission ? permission.userPermissionState === 'active' : true,
    };
    return defaults;
  }

  private getDefaultPermissionsForGroup(
    group: UserGroup | null | undefined,
    allPermissions: Permission[],
  ): any {
    const currentPermissions = group?.userPermissions || [];
    const permissions: { [key: string]: boolean } = {};
    allPermissions.forEach((p) => {
      const hasRole =
        currentPermissions.findIndex(
          (cp) => cp.userPermissionID === p.userPermissionID,
        ) >= 0;
      permissions[this.PERMISSION_VAR + p.userPermissionID] = hasRole;
    });
    return permissions;
  }

  public getGroupDefaults(
    group: UserGroup | null | undefined,
    permissions: Permission[],
  ): any {
    const perms = this.getDefaultPermissionsForGroup(group, permissions);
    const defaults: { [key: string]: any } = {
      UserGroupName: group?.userGroupName || '',
      Description: group?.description || '',
      ApplicationID: group?.applicationTitleID || '',
      CategoryTypeID: group?.userCategoryTypeID || '',
      IsActive: group ? group.userGroupState === 'active' : true,
      ...perms,
    };
    return defaults;
  }

  public getPermissionFromFormData(
    id: number | undefined,
    formData: any,
  ): Permission {
    const group: Permission = {
      userPermissionID: id,
      permissionResourceID: formData.ResourceID,
      permissionActionID: formData.ActionID,
      permissionScopeID: formData.ScopeID,
      description: formData.Description || null,
      userPermissionState: formData.IsActive ? 'active' : 'inactive',
    };
    return group;
  }

  private getSelectedPermissions(
    formData: any,
    allPermissions: Permission[],
  ): number[] {
    const permissionIds: number[] = [];
    Object.keys(formData).forEach((key) => {
      if (key.startsWith(this.PERMISSION_VAR)) {
        const s = key.split('_');
        const id = parseInt(s[1], 10);
        const roleIdx = allPermissions.findIndex(
          (p) => p.userPermissionID === id,
        );
        if (roleIdx >= 0 && formData[key]) {
          permissionIds.push(id);
        }
      }
    });
    return permissionIds;
  }

  public getGroupFromFormData(
    id: number | undefined,
    formData: any,
    permissions: Permission[],
  ): UserGroup {
    const permissionIds = this.getSelectedPermissions(formData, permissions);
    const group: UserGroup = {
      userGroupID: id,
      userGroupName: formData.UserGroupName,
      description: formData.Description || null,
      applicationTitleID: formData.ApplicationID,
      userCategoryTypeID: formData.CategoryTypeID,
      userGroupState: formData.IsActive ? 'active' : 'inactive',
      permissions: permissionIds,
    };
    return group;
  }

  private getDefaultGroupsForRole(
    role: UserRole | null | undefined,
    allGroups: UserGroup[],
  ): any {
    const currentGroups = role?.userGroups || [];
    const groups: { [key: string]: boolean } = {};
    allGroups.forEach((g) => {
      const hasRole =
        currentGroups.findIndex((cg) => cg.userGroupID === g.userGroupID) >= 0;
      groups[this.GROUP_VAR + g.userGroupID] = hasRole;
    });
    return groups;
  }

  public getRoleDefaults(
    role: UserRole | null | undefined,
    allGroups: UserGroup[],
  ): any {
    const groups = this.getDefaultGroupsForRole(role, allGroups);
    const defaults: { [key: string]: any } = {
      UserRoleName: role?.userRoleName || '',
      Description: role?.description || '',
      ApplicationID: role?.applicationTitleID || '',
      CategoryTypeID: role?.userCategoryTypeID || '',
      IsDefault: role?.isDefault || false,
      IsActive: role ? role.userRoleState === 'active' : true,
      DisplayOrder: role?.displayOrder || 1,
      ...groups,
    };
    return defaults;
  }

  private getSelectedGroups(formData: any, allGroups: UserGroup[]): number[] {
    const groupIds: number[] = [];
    Object.keys(formData).forEach((key) => {
      if (key.startsWith(this.GROUP_VAR)) {
        const s = key.split('_');
        const id = parseInt(s[1], 10);
        const groupIdx = allGroups.findIndex((p) => p.userGroupID === id);
        if (groupIdx >= 0 && formData[key]) {
          groupIds.push(id);
        }
      }
    });
    return groupIds;
  }

  public getRoleFromFormData(
    id: number | undefined,
    formData: any,
    groups: UserGroup[],
  ): UserRole {
    const groupIds = this.getSelectedGroups(formData, groups);
    const role: UserRole = {
      userRoleID: id,
      userRoleName: formData.UserRoleName,
      description: formData.Description || null,
      applicationTitleBC: formData.ApplicationBC,
      userCategoryTypeBC: formData.CategoryTypeBC,
      applicationTitleID: formData.ApplicationID,
      userCategoryTypeID: formData.CategoryTypeID,
      isDefault: formData.IsDefault,
      displayOrder: parseInt(formData.DisplayOrder, 10),
      userRoleState: formData.IsActive ? 'active' : 'inactive',
      groups: groupIds,
    };
    return role;
  }

  private getExternalUrl(name: string) {
    const { __environment__ } = window as any;
    return __environment__[name];
  }

  public getPortalCallbackURL(portalName: string): string {
    return this.getExternalUrl(
      `REACT_APP_AWS_SIGNOUT_URI_EXTERNAL_${portalName.toUpperCase()}`,
    );
  }

  public getPortalLogoURL(portalName: string): string {
    return `${this.getPortalCallbackURL(portalName)}/${portalName}/brand-logo.png`;
  }

  public getPortalClientBC(portalName: string): string {
    return this.getExternalUrl(
      `REACT_APP_CLIENT_BC_${portalName.toUpperCase()}`,
    );
  }
}

const securityUtils = new SecurityUtils();

export default securityUtils;
